import React, {useEffect, useState} from "react";
import {Button, Modal, Radio} from "antd";

/**
 * Shared "Move email" modal used by the email listing (single + bulk) and email detail pages.
 *
 * Props:
 *  - visible        : boolean
 *  - onClose        : () => void
 *  - folders        : array of {id, name, folderGuid}
 *  - currentFolder  : id of the email's current folder (filtered out of the choices)
 *  - loading        : boolean — disables Cancel and shows loading on Move
 *  - title          : string — e.g. "Move email" or "Move 5 emails"
 *  - onMove         : (folderGuid, folderName) => void
 */
const MoveEmailModal = ({
                            visible,
                            onClose,
                            folders = [],
                            currentFolder,
                            loading = false,
                            title = "Move email",
                            onMove,
                        }) => {
    const [folderGuid, setFolderGuid] = useState("");
    const [folderName, setFolderName] = useState("");

    useEffect(() => {
        if (!visible) {
            setFolderGuid("");
            setFolderName("");
        }
    }, [visible]);

    const handleSelect = (e) => {
        setFolderGuid(e.target.value);
        setFolderName(e.target.id);
    };

    const handleClose = () => {
        if (loading) return;
        onClose?.();
    };

    const handleMove = () => {
        if (!folderGuid) return;
        onMove?.(folderGuid, folderName);
    };

    const choices = folders.filter((item) => item.id !== currentFolder);

    return (
        <Modal
            title={title}
            open={visible}
            onCancel={handleClose}
            centered
            closable={true}
            width={460}
            footer={[
                <Button key="cancel" onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>,
                <Button
                    key="move"
                    type="primary"
                    onClick={handleMove}
                    loading={loading}
                    disabled={!folderGuid}
                >
                    Move
                </Button>,
            ]}
        >
            <div className="text-[13px] text-[#7A849A] mb-4">
                Choose the destination folder.
            </div>
            <Radio.Group className="w-full" value={folderGuid} onChange={handleSelect}>
                <div className="flex flex-col gap-2">
                    {choices.map((item) => (
                        <Radio
                            key={item.folderGuid}
                            id={item.name}
                            value={item.folderGuid}
                            className="!m-0 px-3 py-2 rounded-[8px] border border-[#E4E7EE] hover:border-[var(--primary-color)] transition-colors w-full"
                        >
                            <span className="text-[14px] text-[#0B1220]">{item.name}</span>
                        </Radio>
                    ))}
                    {choices.length === 0 && (
                        <div className="text-[13px] text-[#7A849A] italic text-center py-4">
                            No other folders available.
                        </div>
                    )}
                </div>
            </Radio.Group>
        </Modal>
    );
};

export default MoveEmailModal;
